import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Briefcase, CreditCard, Plus, Users } from 'lucide-react'
import AppShell from '../../components/b2b/AppShell'
import { api, APIError } from '../../api/client'
import { useB2BAuthStore } from '../../stores/useB2BAuthStore'
import { useB2BBillingStore } from '../../stores/useB2BBillingStore'

/**
 * Phase 1 dashboard placeholder. Подтягивает /api/b2b/workspaces/me чтобы убедиться, что
 * workspace+member актуальны (и hydrate'ит store если данные были минимальными после auth-redirect).
 */
export default function DashboardScreen() {
  const member = useB2BAuthStore((s) => s.member)
  const workspace = useB2BAuthStore((s) => s.workspace)
  const updateMember = useB2BAuthStore((s) => s.updateMember)
  const updateWorkspace = useB2BAuthStore((s) => s.updateWorkspace)
  const subscription = useB2BBillingStore((s) => s.subscription)
  const loadSubscription = useB2BBillingStore((s) => s.loadSubscription)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    api.get('/api/b2b/workspaces/me')
      .then((data) => {
        if (cancelled) return
        if (data.member) updateMember(data.member)
        if (data.workspace) updateWorkspace(data.workspace)
      })
      .catch((err) => {
        if (cancelled) return
        if (!(err instanceof APIError) || err.status >= 500) {
          setError('Could not load workspace. Try refreshing.')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    loadSubscription()
    return () => { cancelled = true }
  }, [updateMember, updateWorkspace, loadSubscription])

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-5xl mx-auto">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-ink-3">Workspace</p>
            <h1 className="text-2xl md:text-3xl font-bold text-ink-1 mt-1">
              {workspace?.name || 'Loading...'}
            </h1>
            <p className="text-sm text-ink-3 mt-1">
              Welcome{member?.name ? `, ${member.name}` : ''}.
            </p>
          </div>
          <Link
            to="/b2b/jobs/new"
            className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-5 py-3 rounded-2xl transition"
          >
            <Plus className="w-4 h-4" />
            New job
          </Link>
        </div>

        {error && (
          <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {subscription && <QuotaWidget subscription={subscription} />}

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link
            to="/b2b/jobs"
            className="group bg-white rounded-3xl shadow-card hover:shadow-card-hover p-6 transition"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-accent/10 text-accent flex items-center justify-center">
                <Briefcase className="w-5 h-5" />
              </div>
              <h2 className="text-lg font-bold text-ink-1">Jobs</h2>
            </div>
            <p className="mt-3 text-sm text-ink-2">
              Create job briefs and screen candidates against them.
            </p>
            <p className="mt-4 text-sm font-bold text-accent group-hover:underline">View jobs →</p>
          </Link>

          <Link
            to="/b2b/candidates"
            className="group bg-white rounded-3xl shadow-card hover:shadow-card-hover p-6 transition"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-accent/10 text-accent flex items-center justify-center">
                <Users className="w-5 h-5" />
              </div>
              <h2 className="text-lg font-bold text-ink-1">Candidates</h2>
            </div>
            <p className="mt-3 text-sm text-ink-2">
              Review candidates submitted across your active jobs.
            </p>
            <p className="mt-4 text-sm font-bold text-accent group-hover:underline">View candidates →</p>
          </Link>
        </div>

        {loading && (
          <p className="mt-8 text-xs text-ink-3">Syncing workspace…</p>
        )}
      </div>
    </AppShell>
  )
}

function QuotaWidget({ subscription }) {
  const plan = subscription.plan
  const quota = subscription.quota
  const used = quota?.used ?? 0
  const limit = quota?.limit ?? 0
  const isUnlimited = limit === -1
  const percent = isUnlimited || limit === 0 ? 0 : Math.min(100, Math.round((used / limit) * 100))
  return (
    <Link
      to="/b2b/billing"
      className="mt-8 block bg-white rounded-3xl shadow-card hover:shadow-card-hover p-5 transition"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent/10 text-accent flex items-center justify-center">
            <CreditCard className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-ink-3">Plan</p>
            <p className="text-base font-bold text-ink-1">{plan?.plan_name || '—'}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs font-bold uppercase tracking-wider text-ink-3">
            {quota?.period === 'lifetime' ? 'Lifetime' : quota?.period}
          </p>
          <p className="text-base font-bold text-ink-1">
            {used} / {isUnlimited ? '∞' : limit}{' '}
            <span className="text-xs font-medium text-ink-3">screenings</span>
          </p>
        </div>
      </div>
      {!isUnlimited && (
        <div className="mt-3 h-1.5 bg-canvas rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${percent >= 90 ? 'bg-red-500' : percent >= 75 ? 'bg-amber-500' : 'bg-accent'}`}
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
    </Link>
  )
}
